import { SceneDefinition, SceneRenderer } from "./sceneRenderer.class";
import { Drag2Rotation } from "./drag2Rotation.class";
import { DragZoom, DragZoomOptions } from "./dragZoom.class";
import { ZoneOverEvent } from "./zone.class";
import { TransitionManager } from "./transitionManager.class";

type PlayerOptions = {
  passive?: boolean;
  rotateStep?: number;
  zoomStep?: number;
  dragZoom?: DragZoomOptions;
};

class ScenePlayer {
  public onAngleChange: (val: number) => void;
  private domElement: HTMLElement;
  private passive: boolean;
  private renderer: SceneRenderer;
  private rotator: Drag2Rotation;
  private dragZoom: DragZoom;
  private transitionManager: TransitionManager;
  private rotateStep: number;
  private zoomStep: number;

  constructor(options?: PlayerOptions) {
    this.renderer = new SceneRenderer();
    this.domElement = this.renderer.canvas;
    this.passive = options?.passive as boolean;
    this.rotateStep = options?.rotateStep || 25;
    this.zoomStep = options?.zoomStep || 100;
    if (!this.passive) {
      this.renderer.resize({ width: 1920, height: 1080 });
      this.rotator = new Drag2Rotation(this.renderer.canvas);
      this.rotator.onAngleChange = this.onRotate.bind(this);
      this.dragZoom = new DragZoom(this.renderer.canvas, options?.dragZoom);
      this.dragZoom.onChange = this.onDragZoom.bind(this);
      this.domElement = this.dragZoom.wrapper;
    }
    this.transitionManager = new TransitionManager(this.renderer.canvas);
  }

  onRotate(val: number) {
    this.renderer.setAngle(val);
    this.onAngleChange?.(val);
  }

  rotateLeft() {
    const angle = this.renderer.getAngle() % 360;
    this.rotator.gotoAngle(angle - this.rotateStep);
  }

  rotateRight(val: number) {
    const angle = this.renderer.getAngle() % 360;
    this.rotator.gotoAngle(angle + this.rotateStep);
  }

  onDragZoom() {
    this.renderer.camera.updateProjectionMatrix();
    this.renderer.follow();
  }

  rotate() {
    this.rotator.rotate();
  }

  gotoAngle(angle: number) {
    this.rotator.gotoAngle(angle);
  }

  gotoZone(key: string) {
    const angle = this.renderer.getNearestFrameForZone(key);
    this.gotoAngle(angle);
  }

  async setScene(scene: SceneDefinition, empty: boolean = false) {
    if (empty) {
      this.renderer.clearCanvas();
    }
    this.transitionManager.initTransition();
    await this.renderer.setScene(scene);
    return await this.transitionManager.startTransition();
  }

  incZoom(val: number) {
    this.dragZoom.incZoom(val);
  }

  zoomIn() {
    this.incZoom(this.zoomStep);
  }

  zoomOut() {
    this.incZoom(-1 * this.zoomStep);
  }

  resize() {
    const parent = this.domElement.parentElement;
    if (!parent) return;
    const { width, height } = parent.getBoundingClientRect();
    const newSize = { width, height };
    if (width && height) {
      if (this.passive) {
        this.renderer.resize(newSize);
      } else {
        this.dragZoom.resize(newSize);
      }
    }
  }

  init() {
    this.resize();
  }

  getDomElement() {
    return this.domElement;
  }

  setHandler(name: string, handler: (event?: ZoneOverEvent) => void) {
    const rendererHandlers = [
      "onZoneLeave",
      "onZoneEnter",
      "onZoneFollow",
      "onZoneClick",
      "onClickOut",
    ];
    if (rendererHandlers.includes(name)) {
      this.renderer.setHandler(name, this.handlerDecorator(handler));
    }
  }

  handlerDecorator(
    handler: (event?: ZoneOverEvent) => void
  ): (event?: ZoneOverEvent) => void {
    return (event?: ZoneOverEvent) => {
      if (this.dragZoom && event?.data?.barycentre) {
        const {
          translate: { x: tx, y: ty },
          size,
          elementSize,
          baseScale,
          zoom,
        }: any = this.dragZoom;
        const { x: basex, y: basey } = event.data.barycentre;
        const scale = zoom * baseScale;
        const zoomed = {
          x:
            (basex / scale - elementSize.width / 2) * scale +
            tx +
            size.width / 2,
          y:
            (basey / scale - elementSize.height / 2) * scale +
            ty +
            size.height / 2,
        };
        event.data.barycentre = zoomed;
      }
      return handler(event);
    };
  }

  setHandlers(handlers: any) {
    for (let name in handlers) {
      this.setHandler(name, handlers[name]);
    }
  }

  setMouseDragMode(active: boolean) {
    this.rotator.setMouseRotateMode(!active);
    this.dragZoom.setMouseDragMode(active);
  }

  setZoomOnWheel(active: boolean) {
    this.dragZoom.setZoomOnWheel(active);
  }

  setZoom(val: number) {
    if (this.dragZoom) {
      this.dragZoom.setZoom(val);
    }
  }
}

export { ScenePlayer };
